import { useEffect, useRef, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { match } from 'ts-pattern';

import {
    EmailVerificationLinkSentToast,
    emailVerificationStatusOptions,
    resendEmailVerificationOptions,
} from '@/entities/user';

import type { EmailVerificationOutcome } from './profile-search';

type VerificationDialog = 'check-inbox' | 'expired';

export function useEmailVerification(
    outcome: EmailVerificationOutcome | undefined,
    onOutcomeHandled: () => void,
) {
    const { data: status } = useQuery(emailVerificationStatusOptions());
    const [openDialog, setOpenDialog] = useState<VerificationDialog | null>(null);
    const handledRef = useRef(false);

    // One mutation per entry point, each confirming through the options rather than a per-call
    // callback: the Settings button explains what to expect in a dialog, the expired dialog toasts.
    const resendFromSettingsMutation = useMutation(
        resendEmailVerificationOptions(() => setOpenDialog('check-inbox')),
    );
    const requestNewLinkMutation = useMutation(
        resendEmailVerificationOptions(() => {
            setOpenDialog(null);
            toast.success(<EmailVerificationLinkSentToast />);
        }),
    );

    useEffect(() => {
        if (!outcome || handledRef.current) return;
        // /auth/verify-email reports a spent token and an unknown one identically to avoid leaking
        // account existence, so the authenticated status is what separates "the link really did
        // expire" from "this link was already used and you're verified". Wait for it.
        if (outcome === 'expired' && status === undefined) return;

        handledRef.current = true;

        match(outcome)
            .with('expired', () => {
                if (!status?.verified) setOpenDialog('expired');
            })
            .with('failed', () => {
                toast.error("We couldn't verify your email. Request a new link and try again.");
            })
            .exhaustive();

        // Clears the flag so a refresh doesn't replay the toast or reopen the dialog.
        onOutcomeHandled();
    }, [outcome, status, onOutcomeHandled]);

    return {
        email: status?.email,
        isVerified: status?.verified,
        isResending: resendFromSettingsMutation.isPending || requestNewLinkMutation.isPending,
        openDialog,
        closeDialog: () => setOpenDialog(null),

        resendFromSettings: () => resendFromSettingsMutation.mutate(),
        requestNewLink: () => requestNewLinkMutation.mutate(),
    };
}
